// useRememberedWindowScroll — put a hub page back where the operator left it
// (W-14 / OR-03).
//
// Home is a hub: scroll down to a tile, work that page, come back. App's
// <ScrollToTop/> resets the window on every pathname change, so the bottom
// bar's Home tab — a fresh navigation — always opened Home at the top, and
// reaching Fuel again meant 500–1 200px of scrolling every time. Back only
// restored it when the browser's own history restoration happened to win the
// race with ScrollToTop (at 390 and 1024 it did; on a 768 tablet it did not).
//
// The position is remembered in MEMORY for this page load only: a reload — and
// a tenant switch, which reloads — starts the page at the top, as it should.
// It is keyed by the caller (Home keys on tenant + app mode, so the farm and
// packhouse views of one tenant don't share an offset).

import { useEffect, useRef, type RefObject } from 'react'

const positions = new Map<string, number>()

/** Test seam — the memory is module-level by design. */
export function forgetRememberedScroll(): void {
  positions.clear()
}

const USER_INPUT = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const

/** Something that scrolls vertically — the window, or an inner scroller. */
export interface ScrollBox { get(): number; set(y: number): void }
export const windowBox: ScrollBox = {
  get: () => window.scrollY,
  set: y => window.scrollTo(0, y),
}
export const elementBox = (el: HTMLElement): ScrollBox => ({
  get: () => el.scrollTop,
  set: y => { el.scrollTop = y },
})

/**
 * Hold `box` at `target()` until it stays there for a few frames — the
 * content that gives the page its height may still be arriving, and
 * ScrollToTop's own frame may land after ours. `target` is re-read every frame
 * (a register aims at a ROW, which moves while the rows above it render).
 * Gives up after `budgetMs`, and at once if the operator touches, clicks,
 * scrolls or types: their scroll wins. `onSettled` runs when it finishes by
 * itself (held, gave up, or the operator took over) — not when the returned
 * cancel is called.
 */
export function settleScroll(
  box: ScrollBox, target: () => number, onSettled?: () => void, budgetMs = 1500,
): () => void {
  let raf = 0
  let stable = 0
  let over = false
  const started = performance.now()
  const end = (settled: boolean) => {
    if (over) return
    over = true
    cancelAnimationFrame(raf)
    for (const e of USER_INPUT) window.removeEventListener(e, takeOver)
    if (settled) onSettled?.()
  }
  const takeOver = () => end(true)
  for (const e of USER_INPUT) window.addEventListener(e, takeOver, { passive: true })
  const tick = () => {
    const t = target()
    if (Math.abs(box.get() - t) <= 1) stable += 1
    else { stable = 0; box.set(t) }
    if (stable >= 3 || performance.now() - started > budgetMs) { end(true); return }
    raf = requestAnimationFrame(tick)
  }
  raf = requestAnimationFrame(tick)
  return () => end(false)
}

/**
 * Remember the window's scroll offset for `key` while `rootRef` is on screen,
 * and restore it once per mount as soon as `ready` (the page's content is
 * rendered). `key` null = not yet known; nothing is recorded or restored.
 */
export function useRememberedWindowScroll(
  key: string | null, ready: boolean, rootRef: RefObject<HTMLElement>,
): void {
  // Read the saved offset on the first render that has a key — BEFORE any
  // scroll event of this mount can overwrite it (ScrollToTop's reset fires
  // one, and it can land before the content is ready).
  const target = useRef<number | null | undefined>(undefined)
  if (key && target.current === undefined) target.current = positions.get(key) ?? null

  useEffect(() => {
    if (!key) return
    // Only while our root is in the document: when the operator leaves, the
    // next page's layout clamps window.scrollY, and that clamped value is not
    // where they left this page.
    const onScroll = () => { if (rootRef.current?.isConnected) positions.set(key, window.scrollY) }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [key, rootRef])

  // Once per mount: the saved offset is consumed when the settle finishes, so a
  // later re-render can never pull the operator back. The cleanup only CANCELS
  // (StrictMode's mount → unmount → mount, or leaving mid-settle), which leaves
  // the offset in place for the next run.
  useEffect(() => {
    const t = target.current
    if (!ready || !t) return
    return settleScroll(windowBox, () => t, () => { target.current = null })
  }, [ready, key])
}
